import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { trackPixel } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import PublicNav from "@/components/PublicNav";
import { toast } from "sonner";
import {
  MapPin, Calendar, Users, Clock, CheckCircle2, Target,
  Loader2, ChevronLeft, Sparkles, ArrowLeft,
} from "lucide-react";

const MODE_LABEL = { onsite: "حضوري", remote: "عن بُعد", hybrid: "هجين" };

function seatsAvailableFor(allocation) {
  if (!allocation) return 0;
  return Math.max(0, (allocation.seats || 0) - (allocation.seats_filled || 0));
}

function describeAllocation(allocation) {
  const seatsLeft = seatsAvailableFor(allocation);
  const parts = [];
  const mode = allocation.delivery_mode || "onsite";
  if (mode === "onsite") {
    if (allocation.city) parts.push(allocation.city);
  } else if (mode === "hybrid") {
    parts.push("هجين");
    if (allocation.city) parts.push(allocation.city);
  } else {
    parts.push("عن بُعد");
  }
  if (allocation.association_name) parts.push(allocation.association_name);
  parts.push(`${seatsLeft} مقعد متاح`);
  return parts.join(" — ");
}

function opportunityPixelData(opportunity, opportunityId, allocation = null) {
  return {
    opportunity_id: String(opportunityId),
    content_ids: [String(opportunityId)],
    content_type: "training_opportunity",
    content_name: opportunity?.title || "Training Opportunity",
    content_category: opportunity?.category || opportunity?.program_name || "training",
    delivery_mode: allocation?.delivery_mode || opportunity?.delivery_mode || "unspecified",
  };
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [opp, setOpp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [motivation, setMotivation] = useState("");
  const [experience, setExperience] = useState("");
  const [applied, setApplied] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState("");
  const viewedOpportunityRef = useRef(null);
  const startedApplicationRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/opportunities/${id}`);
        setOpp(data.opportunity);
        if (user && user.role === "student") {
          const { data: apps } = await api.get("/applications/mine");
          if (apps.applications.some((a) => a.opportunity_id === id)) setApplied(true);
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, user]);

  useEffect(() => {
    if (!opp || viewedOpportunityRef.current === id) return;
    viewedOpportunityRef.current = id;
    trackPixel("ViewContent", opportunityPixelData(opp, id));
  }, [id, opp]);

  const allocations = useMemo(
    () => (opp && Array.isArray(opp.association_allocations) ? opp.association_allocations : []),
    [opp],
  );

  const openAllocations = useMemo(
    () => allocations.filter((a) => seatsAvailableFor(a) > 0),
    [allocations],
  );

  // Suggest a default: single open allocation OR one matching the student's city.
  useEffect(() => {
    if (!opp || allocations.length === 0) return;
    if (selectedAssignmentId) return;
    if (openAllocations.length === 1) {
      setSelectedAssignmentId(openAllocations[0].id);
      return;
    }
    if (user?.city) {
      const cityMatch = openAllocations.find(
        (a) => (a.delivery_mode || "onsite") !== "remote" && (a.city || "") === user.city,
      );
      if (cityMatch) setSelectedAssignmentId(cityMatch.id);
    }
  }, [opp, allocations, openAllocations, selectedAssignmentId, user]);

  const selectedAllocation = useMemo(
    () => allocations.find((a) => a.id === selectedAssignmentId) || null,
    [allocations, selectedAssignmentId],
  );

  const openApplication = () => {
    if (user?.role === "student" && startedApplicationRef.current !== id) {
      startedApplicationRef.current = id;
      trackPixel("StartApplication", opportunityPixelData(opp, id, selectedAllocation));
    }
    setShowModal(true);
  };

  const handleApply = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/opportunities/${id}` } });
      return;
    }
    if (user.role !== "student") {
      toast.error("التقديم متاح للطلاب فقط");
      return;
    }
    if (allocations.length > 0 && !selectedAssignmentId) {
      toast.error("اختر موقع تنفيذ قبل التأكيد");
      return;
    }
    setApplying(true);
    try {
      const payload = {
        opportunity_id: id,
        motivation,
        experience,
      };
      if (selectedAssignmentId) {
        payload.opportunity_assignment_id = selectedAssignmentId;
      }
      await api.post("/applications", payload);
      trackPixel("SubmitApplication", {
        ...opportunityPixelData(opp, id, selectedAllocation),
        status: "submitted",
      });
      toast.success("تم إرسال طلبك بنجاح 🎉");
      setApplied(true);
      setShowModal(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-10 w-10 animate-spin text-teal-700" />
        </div>
      </div>
    );
  }

  if (!opp) return null;

  // Seats overview — show allocation-specific numbers once a location is picked.
  const totalSeats = allocations.length > 0
    ? allocations.reduce((sum, a) => sum + (a.seats || 0), 0)
    : (opp.seats || 0);
  const totalFilled = allocations.length > 0
    ? allocations.reduce((sum, a) => sum + (a.seats_filled || 0), 0)
    : (opp.seats_filled || 0);
  const displaySeats = selectedAllocation
    ? { total: selectedAllocation.seats || 0, filled: selectedAllocation.seats_filled || 0 }
    : { total: totalSeats, filled: totalFilled };
  const seatsLeft = Math.max(0, displaySeats.total - displaySeats.filled);
  const cannotApply = allocations.length > 0
    ? openAllocations.length === 0
    : seatsLeft <= 0;

  // Card headline: chosen city if any, or a summary of allocation cities.
  const cardCity = selectedAllocation
    ? (selectedAllocation.delivery_mode === "remote"
        ? "عن بُعد"
        : selectedAllocation.city || opp.city)
    : (allocations.length > 0
        ? [...new Set(
            allocations
              .filter((a) => (a.delivery_mode || "onsite") !== "remote")
              .map((a) => a.city)
              .filter(Boolean),
          )].join(" · ") || (allocations.some((a) => (a.delivery_mode || "onsite") === "remote") ? "عن بُعد" : "")
        : opp.city);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="opportunity-detail">
      <PublicNav />

      <section className="relative">
        <div className="relative min-h-[16rem] lg:min-h-[20rem] bg-slate-800 overflow-hidden">
          <img
            src={opp.cover_image_url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 pointer-events-none bg-gradient-to-t from-slate-950/85 via-slate-950/55 to-slate-950/20"
            aria-hidden="true"
          />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 sm:pt-20 pb-8 min-h-[16rem] lg:min-h-[20rem] flex flex-col justify-end gap-3">
            <div className="flex items-center gap-2 text-xs text-white/90 flex-wrap">
              <Link to="/opportunities" className="hover:text-white underline-offset-2 hover:underline">فرص التدريب التعاوني</Link>
              <ChevronLeft className="h-3 w-3 shrink-0" />
              <span className="truncate max-w-[60vw]">{opp.category}</span>
            </div>
            {opp.category && (
              <div
                className="self-start inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-teal-800 text-xs font-bold shadow-md ring-1 ring-black/5"
                data-testid="opp-detail-category"
              >
                {opp.category}
              </div>
            )}
            <h1 className="text-2xl lg:text-5xl font-heading font-extrabold text-white leading-snug break-words max-w-4xl">
              {opp.title}
            </h1>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="p-5 rounded-2xl bg-white border border-slate-200">
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">وصف فرصة التدريب التعاوني</h2>
            <p className="text-slate-700 leading-loose whitespace-pre-line">{opp.description}</p>
          </div>

          {allocations.length > 0 && (
            <div className="p-5 rounded-2xl bg-white border border-slate-200" data-testid="opp-detail-allocations">
              <h2 className="text-xl font-heading font-bold text-slate-900 mb-4">
                مواقع التنفيذ ({allocations.length})
              </h2>
              <ul className="grid gap-3 sm:grid-cols-2">
                {allocations.map((a) => {
                  const left = seatsAvailableFor(a);
                  const mode = a.delivery_mode || "onsite";
                  return (
                    <li
                      key={a.id}
                      className={`rounded-xl border p-4 ${
                        left <= 0
                          ? "border-slate-200 bg-slate-50 opacity-70"
                          : "border-teal-100 bg-teal-50/40"
                      }`}
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-bold ${
                            mode === "remote"
                              ? "bg-sky-50 text-sky-700"
                              : mode === "hybrid"
                              ? "bg-violet-50 text-violet-700"
                              : "bg-teal-50 text-teal-700"
                          }`}
                        >
                          {MODE_LABEL[mode]}
                        </span>
                        <span className="text-xs font-semibold text-slate-600">
                          {left} / {a.seats} مقعد
                        </span>
                      </div>
                      <div className="text-sm font-bold text-slate-900">
                        {a.association_name}
                      </div>
                      <div className="text-xs text-slate-500">
                        {mode === "remote" ? "لا تنطبق مدينة" : a.city}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {opp.requirements?.length > 0 && (
            <div className="p-5 rounded-2xl bg-white border border-slate-200">
              <h2 className="text-xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-teal-700" />
                الشروط والمتطلبات
              </h2>
              <ul className="space-y-3">
                {opp.requirements.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {opp.outcomes?.length > 0 && (
            <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-50 to-white border border-amber-100">
              <h2 className="text-xl font-heading font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-600" />
                المخرجات المتوقعة
              </h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {opp.outcomes.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="p-4 rounded-2xl bg-white border border-slate-200 card-shadow">
            <div className="space-y-4 text-sm">
              {cardCity && <InfoRow icon={MapPin} label="المدينة" value={cardCity} />}
              <InfoRow icon={Clock} label="المدة" value={`${opp.duration_weeks} أسبوع`} />
              {opp.total_training_hours && <InfoRow icon={Clock} label="إجمالي الساعات التدريبية" value={`${opp.total_training_hours} ساعة`} />}
              <InfoRow
                icon={Users}
                label={selectedAllocation ? "مقاعد الموقع المحدد" : "إجمالي المقاعد المتاحة"}
                value={`${seatsLeft} من ${displaySeats.total}`}
              />
              {opp.application_end && (
                <InfoRow icon={Calendar} label="آخر موعد للتقديم" value={new Date(opp.application_end).toLocaleDateString("ar-SA")} />
              )}
            </div>

            <button
              onClick={
                applied
                  ? undefined
                  : !user
                  ? () => navigate("/login", { state: { from: `/opportunities/${id}` } })
                  : openApplication
              }
              disabled={applied || cannotApply}
              className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-all
                ${applied ? "bg-emerald-50 text-emerald-700 border border-emerald-100 cursor-default"
                         : cannotApply ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                         : "bg-teal-700 text-white hover:bg-teal-600 hover:-translate-y-0.5 shadow-lg shadow-teal-700/20"}`}
              data-testid="apply-opportunity-btn"
            >
              {applied ? (<><CheckCircle2 className="h-5 w-5" /> تم تقديم طلبك</>)
                : cannotApply ? "لا توجد مقاعد متاحة"
                : (<>تقديم على الفرصة <ArrowLeft className="h-5 w-5" /></>)}
            </button>

            {!user && (
              <p className="mt-3 text-xs text-slate-500 text-center">
                <Link to="/login" className="text-teal-700 font-semibold hover:underline">سجّل الدخول</Link>
                {" "}للتقديم على الفرصة
              </p>
            )}
          </div>

          {opp.tags?.length > 0 && (
            <div className="p-4 rounded-2xl bg-white border border-slate-200">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">الوسوم</div>
              <div className="flex flex-wrap gap-2">
                {opp.tags.map((t) => (
                  <span key={t} className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </aside>
      </section>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-4" onClick={() => setShowModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-5" onClick={(e) => e.stopPropagation()} data-testid="apply-modal">
            <h3 className="text-2xl font-heading font-extrabold text-slate-900">التقديم على الفرصة</h3>
            <p className="mt-1 text-sm text-slate-600">شاركنا دوافعك وخبرتك لمساعدتنا في تقييم طلبك.</p>

            <div className="mt-6 space-y-4">
              {allocations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    موقع التنفيذ المفضّل
                  </label>
                  <select
                    value={selectedAssignmentId}
                    onChange={(e) => setSelectedAssignmentId(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                    data-testid="apply-assignment-select"
                  >
                    <option value="">اختر موقعًا</option>
                    {allocations.map((a) => {
                      const left = seatsAvailableFor(a);
                      const disabled = left <= 0;
                      return (
                        <option
                          key={a.id}
                          value={a.id}
                          disabled={disabled}
                        >
                          {describeAllocation(a)}
                          {disabled ? " — مكتمل" : ""}
                        </option>
                      );
                    })}
                  </select>
                  <p className="mt-2 text-xs text-slate-500">
                    هذا الاختيار تفضيل فقط. التسكين النهائي بيد النظام.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">دوافعك للانضمام</label>
                <textarea
                  rows={3}
                  value={motivation}
                  onChange={(e) => setMotivation(e.target.value)}
                  placeholder="لماذا تريد الانضمام لهذه الفرصة؟"
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                  data-testid="motivation-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">خبرتك السابقة (اختياري)</label>
                <textarea
                  rows={3}
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  placeholder="اذكر أي خبرات ذات صلة..."
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
                  data-testid="experience-input"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-5 py-3 rounded-lg text-slate-700 font-medium hover:bg-slate-100 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleApply}
                disabled={
                  applying
                  || (allocations.length > 0 && !selectedAssignmentId)
                }
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60 transition-colors"
                data-testid="confirm-apply-btn"
              >
                {applying && <Loader2 className="h-4 w-4 animate-spin" />}
                تأكيد التقديم
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-teal-50 grid place-items-center text-teal-700 shrink-0">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
      </div>
      <div>
        <div className="text-xs text-slate-500">{label}</div>
        <div className="font-semibold text-slate-900">{value}</div>
      </div>
    </div>
  );
}
